import type { RucStatus } from "@/domains/ruc";
import type { FuelType } from "./types";

/**
 * Distance buffer (km) before a vehicle's current RUC license runs out that
 * counts as "coming due" rather than fine. Set to 500km - half of a
 * standard 1000km RUC block - for now; expected to become a
 * per-fleet/vehicle configurable setting later.
 */
export const RUC_COMING_DUE_THRESHOLD_KM = 500;

/**
 * NZ light petrol/hybrid vehicles don't pay RUC today - they pay fuel
 * excise duty at the pump instead (see useTripCostEstimates.ts's
 * paysRucToday, which this mirrors). A planned 2027/28 extension will bring
 * them into RUC, but until then a RUC status/license on one of these
 * vehicles - real or simulated - doesn't represent a charge they're
 * actually liable for, so it shouldn't drive alerts, filters, or
 * fleet-wide counts. `fuelType` accepts `null`/`undefined` (Vehicle.fuelType
 * is nullable) - an unknown fuel type is treated as RUC-liable rather than
 * silently exempted.
 */
export function vehiclePaysRucToday(fuelType: FuelType | null | undefined): boolean {
  return fuelType !== "PETROL" && fuelType !== "HYBRID";
}

export type VehicleRucFlags = {
  /** Vehicle's odometer has already passed its current RUC license's covered distance. */
  isOverdue: boolean;
  /** Not overdue, but within RUC_COMING_DUE_THRESHOLD_KM of running out. */
  isComingDue: boolean;
};

/**
 * Vehicle-level RUC classification from its `RucStatus` row.
 *
 * `rucStatus` is `undefined` for a vehicle with no RUC license on file -
 * that's neither overdue nor coming due, it's simply unmeasured. A vehicle
 * that doesn't pay RUC today (see vehiclePaysRucToday) is likewise never
 * overdue/coming due, regardless of what its RucStatus row says.
 */
export function getVehicleRucFlags(
  rucStatus: RucStatus | undefined,
  fuelType: FuelType | null | undefined,
): VehicleRucFlags {
  if (!rucStatus || !vehiclePaysRucToday(fuelType)) {
    return { isOverdue: false, isComingDue: false };
  }

  return {
    isOverdue: rucStatus.km_remaining < 0,
    isComingDue:
      rucStatus.km_remaining >= 0 &&
      rucStatus.km_remaining <= RUC_COMING_DUE_THRESHOLD_KM,
  };
}

/**
 * One stat from VehicleIssuesSummaryCard.vue's RUC breakdown. Mirrors
 * VehicleIssueFilter's role for compliance - lets a click on either RUC stat
 * filter VehicleList.vue down to exactly the vehicles it counted.
 */
export type VehicleRucFilter = "rucOverdue" | "rucComingDue";

export const VEHICLE_RUC_FILTER_OPTIONS: {
  value: VehicleRucFilter;
  label: string;
}[] = [
  { value: "rucOverdue", label: "RUC overdue" },
  {
    value: "rucComingDue",
    label: `RUC due within ${RUC_COMING_DUE_THRESHOLD_KM} km`,
  },
];

/**
 * Tests a single vehicle's RUC status against one `VehicleRucFilter` value.
 * Mirrors the per-stat logic in VehicleIssuesSummaryCard.vue exactly, so a
 * vehicle only matches a filter if it's one of the vehicles that stat
 * counted.
 */
export function vehicleMatchesRucFilter(
  filter: VehicleRucFilter,
  rucStatus: RucStatus | undefined,
  fuelType: FuelType | null | undefined,
): boolean {
  const flags = getVehicleRucFlags(rucStatus, fuelType);

  switch (filter) {
    case "rucOverdue":
      return flags.isOverdue;
    case "rucComingDue":
      return flags.isComingDue;
  }
}
